import React from 'react';
import { Mic, MessageCircleQuestion, Phone, ShieldCheck, UserRound } from 'lucide-react';

import CallingVoiceSettings from './CallingVoiceSettings';
import CallingVoiceCatalogSettings from './CallingVoiceCatalogSettings';
import CallingSpeechToTextSettings from './CallingSpeechToTextSettings';
import CallingNoiseSettings from './CallingNoiseSettings';
import CallingBehaviorSettings from './CallingBehaviorSettings';
import CallingOperationsSettings from './CallingOperationsSettings';
import CallingVocabularySettings from './CallingVocabularySettings';
import CallingAlertsBanner from './CallingAlertsBanner';

// 2026-09-22 (Docs/CALLING_ELEVENLABS_STT_OPTION_PLAN.md, user feedback:
// "does not look user friendly" on the previous flat stack of cards):
// replaces SettingsPage.tsx's old `<CallingVoiceSettings /><CallingBehaviorSettings />`
// stack with three named sections (Voice / Speech-to-Text / Call Behavior)
// under one sticky side-nav that jump-scrolls between them -- everything
// stays on one page (no tabs, no hidden content), per the approved mockup
// (Docs/mockups/ai-calling-settings-full.html). Speech-to-Text was split
// out of CallingVoiceSettings.tsx into its own component
// (CallingSpeechToTextSettings.tsx) specifically so it could get its own
// section here.
const SECTIONS = [
  { id: 'voice', label: 'Voice', icon: Mic },
  // 2026-09-30 (Docs/VOICE_CATALOG_CALLER_VOICE_CHOICE_PLAN.md)
  { id: 'voice-choices', label: 'Voice Choices', icon: UserRound },
  { id: 'stt', label: 'Speech-to-Text', icon: Phone },
  { id: 'behavior', label: 'Call Behavior', icon: MessageCircleQuestion },
  // 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md Phase A)
  { id: 'operations', label: 'Operations', icon: ShieldCheck },
];

export default function AICallingSettingsLayout() {
  return (
    <div className="flex items-start gap-6">
      <nav className="sticky top-0 hidden w-44 shrink-0 flex-col gap-0.5 self-start py-1 lg:flex">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <a
            key={id}
            href={`#ai-calling-${id}`}
            className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground no-underline hover:bg-accent hover:text-foreground"
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            {label}
          </a>
        ))}
        <p className="mt-3 px-2.5 text-xs leading-relaxed text-muted-foreground">
          Each section saves independently — jump to any part, nothing is hidden.
        </p>
      </nav>

      <div className="min-w-0 flex-1 space-y-10">
        {/* 2026-10-04 (plan 3.4): live problems first. Renders nothing when all is well. */}
        <CallingAlertsBanner />
        <section id="ai-calling-voice" className="scroll-mt-4">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <Mic className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Voice</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            Which text-to-speech engine the AI uses on live calls. Falls back automatically if the
            chosen provider fails.
          </p>
          <CallingVoiceSettings />
        </section>

        <section id="ai-calling-voice-choices" className="scroll-mt-4 border-t border-border/60 pt-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <UserRound className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Voice Choices</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            Voices callers and WhatsApp users can switch to by asking. Off by default.
          </p>
          <CallingVoiceCatalogSettings />
        </section>

        <section id="ai-calling-stt" className="scroll-mt-4 border-t border-border/60 pt-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <Phone className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Speech-to-Text</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            Which engine transcribes the caller's voice, and its fallback behavior.
          </p>
          <CallingSpeechToTextSettings />
          {/* 2026-10-04: background-noise handling, next to the STT engine it affects. */}
          <div className="mt-6">
            <CallingNoiseSettings />
          </div>
        </section>

        <section id="ai-calling-behavior" className="scroll-mt-4 border-t border-border/60 pt-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <MessageCircleQuestion className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Call Behavior</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            The opening line, personality, and configurable questions the AI asks callers.
          </p>
          <CallingBehaviorSettings />
          {/* 2026-10-04 (plan 4.3): pronunciations + speech-to-text corrections. */}
          <div className="mt-6">
            <CallingVocabularySettings />
          </div>
        </section>

        <section id="ai-calling-operations" className="scroll-mt-4 border-t border-border/60 pt-10">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-bold">Operations</h2>
          </div>
          <p className="mb-4 ml-10 max-w-xl text-sm text-muted-foreground">
            AI disclosure, call limits and alerts — what you need to run the line in production.
          </p>
          <CallingOperationsSettings />
        </section>
      </div>
    </div>
  );
}
